import * as THREE from "three";
import { EDGE_FAR, surfaceAt, type EdgeSide, type Level } from "../level";
import { mergeStatic } from "./merge";
import { inked, toon } from "./toon";

// The beach (#82): a soft side's sand and sea, and the scenery of a
// `look.outside: "beach"` park (palms, a lifeguard tower, a boardwalk and
// dunes). All of it is scenery: `groundKind` in level.ts says what the
// ground is, this only draws it. Fixed layouts, so every build of a map
// looks the same.

/** Which side each wall stands on, in the order they're built (side k is at angle k * 90 degrees). */
export const WALL_SIDES: readonly EdgeSide[] = ["south", "east", "north", "west"];

export const SAND = "#e8d3a2";
const WET_SAND = "#c9b184";
const SEA = "#2f8fc9";
const SHALLOW = "#46c9c9";
const FOAM = "#fbfbf5";

/** How deep the sea's surface sits, so its swells never poke through the sand at the waterline (m). */
const SEA_LEVEL = -0.06;
/** Swell height far out, and how far out it takes to build (m). */
const SWELL = 0.14;
const SWELL_BUILD = 10;

/**
 * A soft side's beach: the sand strip inside the fence line, and past it
 * the sea (or more sand). With the sea, the last metre of sand is wet, foam
 * lies along the waterline and on a breaking line further out, and the
 * water goes from shallow turquoise to deep blue over gentle swells. Side
 * `side` is at angle side * 90 degrees; local +z points out through it.
 */
export function buildBeach(bounds: number, side: number, sand: number, water: boolean): THREE.Object3D {
  const group = new THREE.Group();
  group.rotation.y = (side * Math.PI) / 2;
  const across = bounds * 2;
  const depth = water ? sand : sand + EDGE_FAR;
  const beach = new THREE.Mesh(
    new THREE.PlaneGeometry(across, depth),
    toon(SAND, { roughness: 0.95, polygonOffset: true, polygonOffsetFactor: -1 }, "concrete"),
  );
  beach.rotation.x = -Math.PI / 2;
  beach.position.set(0, 0.002, bounds - sand + depth / 2);
  beach.receiveShadow = true;
  group.add(beach);
  if (!water) return group;

  const wet = new THREE.Mesh(
    new THREE.PlaneGeometry(across, 1.2),
    toon(WET_SAND, { roughness: 0.6, polygonOffset: true, polygonOffsetFactor: -2 }, "concrete"),
  );
  wet.rotation.x = -Math.PI / 2;
  wet.position.set(0, 0.003, bounds - 0.6);
  wet.receiveShadow = true;
  group.add(wet);
  group.add(buildSea(across * 5, EDGE_FAR * 4, bounds));
  const foam = toon(FOAM, { roughness: 0.9 }, "paint");
  group.add(foamLine(across, bounds + 0.15, 0.5, 0.01, foam, 0));
  group.add(foamLine(across, bounds + 4, 0.7, SEA_LEVEL + 0.05, foam, 1.7));
  group.add(foamLine(across * 1.4, bounds + 11, 0.5, SEA_LEVEL + SWELL + 0.03, foam, 3.1));
  return group;
}

/** The sea past the waterline at `shore`: swells parallel to the beach, turquoise in the shallows, blue further out. */
function buildSea(width: number, depth: number, shore: number): THREE.Mesh {
  const geo = new THREE.PlaneGeometry(width, depth, 96, 64);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position!;
  const colors = new Float32Array(pos.count * 3);
  const shallow = new THREE.Color(SHALLOW);
  const deep = new THREE.Color(SEA);
  const c = new THREE.Color();
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const out = pos.getZ(i) + depth / 2; // metres out from the waterline
    const build = Math.min(1, out / SWELL_BUILD);
    const swell = Math.sin(out * 0.9 + Math.sin(x * 0.07) * 1.5) * 0.6 + Math.sin(out * 0.37 - x * 0.05) * 0.4;
    pos.setY(i, SEA_LEVEL + swell * SWELL * build);
    c.copy(shallow).lerp(deep, Math.min(1, out / 14));
    colors.set([c.r, c.g, c.b], i * 3);
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geo.computeVertexNormals();
  const sea = new THREE.Mesh(geo, toon("#ffffff", { roughness: 0.15, metalness: 0.1, vertexColors: true }, "glass"));
  sea.position.z = shore + depth / 2;
  sea.receiveShadow = true;
  return sea;
}

/** A wavy ribbon of foam lying parallel to the shore, `out` from the middle, `width` wide. */
function foamLine(length: number, out: number, width: number, y: number, mat: THREE.Material, phase: number): THREE.Mesh {
  const segs = Math.ceil(length / 1.5);
  const pos: number[] = [];
  const index: number[] = [];
  for (let i = 0; i <= segs; i++) {
    const x = -length / 2 + (i / segs) * length;
    const wiggle = Math.sin(x * 0.23 + phase) * 0.35 + Math.sin(x * 0.61 + phase * 2) * 0.15;
    const w = width * (0.6 + 0.4 * Math.sin(x * 0.4 + phase) ** 2);
    pos.push(x, y, out + wiggle - w / 2, x, y, out + wiggle + w / 2);
    if (i > 0) index.push((i - 1) * 2, (i - 1) * 2 + 1, i * 2, i * 2, (i - 1) * 2 + 1, i * 2 + 1);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setIndex(index);
  geo.computeVertexNormals();
  return new THREE.Mesh(geo, mat);
}

/** Makes a surface material; the scenery shares one per look so `mergeStatic` can batch it. */
type MaterialMaker = typeof toon;

/** A `toon` that hands back the same material for the same look, for one build. */
function sharedMaterials(): MaterialMaker {
  const made = new Map<string, THREE.MeshStandardMaterial>();
  return (color, extra = {}, surface = "plastic") => {
    const key = JSON.stringify([color, extra, surface]);
    const m = made.get(key) ?? toon(color, extra, surface);
    made.set(key, m);
    return m;
  };
}

/** A palm: a leaning, curving trunk in rings, a crown of drooping fronds and a few coconuts. `s` scales it. */
export function buildPalm(x: number, z: number, s: number, lean: number, mat: MaterialMaker = toon): THREE.Object3D {
  const palm = new THREE.Group();
  const bark = mat("#9c7650", { roughness: 0.9 }, "plywood");
  const rings = 7;
  const top = new THREE.Vector3();
  for (let i = 0; i < rings; i++) {
    // The trunk bends further over the higher it gets.
    const t = (i + 0.5) / rings;
    const seg = inked(new THREE.CylinderGeometry(0.17 * s * (1 - t * 0.35), 0.21 * s * (1 - t * 0.3), 1.1 * s, 8), bark, 0.02);
    seg.position.set(Math.sin(lean) * t * t * 1.6 * s, (i + 0.5) * 0.9 * s, 0);
    seg.rotation.z = -lean * t * 0.9;
    seg.castShadow = true;
    palm.add(seg);
    top.copy(seg.position);
  }
  top.y += 0.45 * s;
  const frond = mat("#3f9e4a", { roughness: 0.8, side: THREE.DoubleSide }, "grass");
  const leafGeo = new THREE.ConeGeometry(0.45 * s, 3 * s, 4, 1, true);
  leafGeo.translate(0, 1.5 * s, 0);
  leafGeo.scale(1, 1, 0.25);
  for (let k = 0; k < 8; k++) {
    const leaf = new THREE.Mesh(leafGeo, frond);
    leaf.position.copy(top);
    leaf.rotation.set(0, (k / 8) * Math.PI * 2 + 0.3, 0);
    leaf.rotateZ(-(1.05 + (k % 2) * 0.35)); // out and drooping
    leaf.castShadow = true;
    palm.add(leaf);
  }
  const nut = mat("#6b4a2b", { roughness: 0.7 }, "plywood");
  const nutGeo = new THREE.SphereGeometry(0.16 * s, 8, 6);
  for (let k = 0; k < 3; k++) {
    const coconut = new THREE.Mesh(nutGeo, nut);
    const a = (k / 3) * Math.PI * 2;
    coconut.position.set(top.x + Math.cos(a) * 0.22 * s, top.y - 0.2 * s, top.z + Math.sin(a) * 0.22 * s);
    palm.add(coconut);
  }
  palm.position.set(x, 0, z);
  palm.rotation.y = lean * 2.3;
  return palm;
}

/** A lifeguard tower on stilts: a white hut with a red stripe, a sloped roof, a ramp down to the sand and a flag. Faces -z locally. */
export function buildLifeguardTower(x: number, z: number, yaw: number, mat: MaterialMaker = toon): THREE.Object3D {
  const tower = new THREE.Group();
  const wood = mat("#8d6e52", { roughness: 0.85 }, "plywood");
  const legGeo = new THREE.CylinderGeometry(0.08, 0.09, 2.2, 8);
  for (const [lx, lz] of [
    [-0.9, -0.9],
    [0.9, -0.9],
    [-0.9, 0.9],
    [0.9, 0.9],
  ] as const) {
    const leg = inked(legGeo, wood, 0.012);
    leg.position.set(lx, 1.1, lz);
    tower.add(leg);
  }
  const deck = inked(new THREE.BoxGeometry(2.6, 0.12, 2.6), wood, 0.012);
  deck.position.y = 2.25;
  tower.add(deck);
  const hut = inked(new THREE.BoxGeometry(2, 1.5, 1.8), mat("#f4f1ea", { roughness: 0.6 }, "paint"), 0.015);
  hut.position.set(0, 3.06, 0.2);
  tower.add(hut);
  const stripe = new THREE.Mesh(new THREE.BoxGeometry(2.04, 0.3, 1.84), mat("#e63946", { roughness: 0.6 }, "paint"));
  stripe.position.set(0, 2.7, 0.2);
  tower.add(stripe);
  const glass = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.55, 0.05), mat("#bfe3ff", { roughness: 0.1 }, "glass"));
  glass.position.set(0, 3.25, -0.72);
  tower.add(glass);
  const roof = inked(new THREE.BoxGeometry(2.5, 0.12, 2.4), mat("#4a9fd8", { roughness: 0.5 }, "paint"), 0.015);
  roof.position.set(0, 3.95, 0.1);
  roof.rotation.x = 0.12;
  tower.add(roof);
  const ramp = inked(new THREE.BoxGeometry(0.8, 0.08, 3.2), wood, 0.012);
  ramp.position.set(0, 1.12, 2.6);
  ramp.rotation.x = 0.72;
  tower.add(ramp);
  const pole = inked(new THREE.CylinderGeometry(0.03, 0.03, 1.8, 6), mat("#d4dae3", { roughness: 0.3, metalness: 0.6 }, "metal"), 0.008);
  pole.position.set(0.95, 4.7, 0.9);
  tower.add(pole);
  const flag = new THREE.Mesh(new THREE.PlaneGeometry(0.7, 0.45), mat("#e63946", { roughness: 0.8, side: THREE.DoubleSide }, "cloth"));
  flag.position.set(1.3, 5.35, 0.9);
  tower.add(flag);
  tower.position.set(x, 0, z);
  tower.rotation.y = yaw;
  return tower;
}

/** A boardwalk running along local x from `from` to `to`, centred `at` out along local z, with a handrail on its far side. */
function buildBoardwalk(from: number, to: number, at: number, mat: MaterialMaker): THREE.Object3D {
  const group = new THREE.Group();
  const len = to - from;
  const width = 3;
  const wood = mat("#b08a62", { roughness: 0.8 }, "plywood");
  const walk = inked(new THREE.BoxGeometry(len, 0.1, width), wood, 0.01);
  walk.position.set(-(from + to) / 2, 0.06, at);
  walk.receiveShadow = true;
  group.add(walk);
  // Plank seams across it, one every 1.2 m.
  const seam = mat("#7a5a3c", { roughness: 0.9 }, "plywood");
  const seamGeo = new THREE.BoxGeometry(0.04, 0.02, width);
  for (let t = from + 0.6; t < to; t += 1.2) {
    const s = new THREE.Mesh(seamGeo, seam);
    s.position.set(-t, 0.115, at);
    group.add(s);
  }
  // A handrail along the sea side.
  const rail = inked(new THREE.BoxGeometry(len, 0.08, 0.08), wood, 0.008);
  rail.position.set(-(from + to) / 2, 0.95, at + width / 2 - 0.1);
  group.add(rail);
  const postGeo = new THREE.BoxGeometry(0.1, 0.9, 0.1);
  for (let t = from; t <= to + 1e-6; t += 3) {
    const post = new THREE.Mesh(postGeo, wood);
    post.position.set(-t, 0.5, at + width / 2 - 0.1);
    group.add(post);
  }
  return group;
}

/** A low sand dune: a squashed, half-buried blob. */
function buildDune(x: number, z: number, r: number, h: number, mat: MaterialMaker): THREE.Mesh {
  const dune = new THREE.Mesh(new THREE.SphereGeometry(1, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2), mat(SAND, { roughness: 0.95 }, "concrete"));
  dune.scale.set(r, h, r * 0.7);
  dune.position.set(x, -0.02, z);
  dune.receiveShadow = true;
  return dune;
}

/**
 * Where along a beach (local x) its lifeguard tower stands: three quarters
 * of the way along, unless a piece is there (Sunny Beach v2's pier, #221),
 * then the first other spot with only sand under the tower.
 */
function towerSpot(level: Level, side: number): number {
  const b = level.bounds;
  const turn = (side * Math.PI) / 2;
  const clear = (t: number) => {
    for (const dt of [-1.5, 0, 1.5])
      for (const dz of [-1.5, 0, 1.5]) {
        // Local (x, z) out through this side, turned into the park's frame.
        const x = t + dt;
        const z = b - 1.6 + dz;
        if (surfaceAt(level, x * Math.cos(turn) + z * Math.sin(turn), -x * Math.sin(turn) + z * Math.cos(turn)).h > 0) return false;
      }
    return true;
  };
  return [0.72, -0.6, 0.3].map((f) => f * b).find(clear) ?? b * 0.72;
}

/**
 * What stands round a beach park (`look.outside: "beach"`): a boardwalk,
 * dunes and palms out past each soft side without the sea, a lifeguard
 * tower on each beach with the sea, and palms along the streets behind the
 * walls. Nothing here is in the sim; it all stands off the concrete.
 */
export function buildBeachScenery(level: Level, clear: (side: EdgeSide, t: number) => boolean = () => false): THREE.Object3D {
  const group = new THREE.Group();
  const mat = sharedMaterials();
  const b = level.bounds;
  const edges = level.edges ?? [];
  WALL_SIDES.forEach((sideName, side) => {
    const edge = edges.find((e) => e.side === sideName);
    const around = new THREE.Group();
    around.rotation.y = (side * Math.PI) / 2; // local +z points out through this side, local x runs along it
    if (edge && !edge.water) {
      around.add(buildBoardwalk(-b + 1, b - 1, b + 2.5, mat));
      for (let t = -b + 4, k = 0; t < b; t += 9, k++) {
        around.add(buildPalm(t, b + 6.5 + (k % 2) * 2.5, 1 + (k % 3) * 0.12, 0.25 + (k % 2) * 0.2, mat));
        // Dunes stay between the corners, clear of a neighbouring side's sea.
        const r = 5 + (k % 2) * 2;
        if (Math.abs(t + 4) + r <= b) around.add(buildDune(t + 4, b + 15 + (k % 3) * 3, r, 1.2 + (k % 3) * 0.4, mat));
      }
    } else if (edge) {
      // A lifeguard tower near the waterline, looking out to sea, and palms at the back of the beach.
      around.add(buildLifeguardTower(towerSpot(level, side), b - 1.6, Math.PI, mat));
      for (const [t, s, lean] of [
        [-b * 0.8, 1.15, 0.35],
        [-b * 0.25, 1, 0.5],
        [b * 0.3, 1.2, 0.3],
      ] as const) {
        around.add(buildPalm(t, b - edge.sand * 0.6, s, lean, mat));
      }
    } else {
      // A row of palms along the street behind the wall.
      // None where a street end's street runs out (#236, `clear`, along the side as it's built).
      for (let t = -b + 5, k = 0; t < b; t += 11, k++) if (!clear(sideName, t)) around.add(buildPalm(t, b + 4, 1.1 + (k % 2) * 0.15, 0.12, mat));
    }
    // None of it moves, so each side draws as one mesh per material (#89);
    // per side rather than all at once, so what's out of view still culls.
    group.add(mergeStatic(around));
  });
  return group;
}
